import { useOrganization } from '@/contexts/useOrganization'
import {
  Accordion,
  Box,
  Button,
  Field,
  HStack,
  IconButton,
  Input,
  Switch,
  Tabs,
  Tag,
  Tooltip,
  VStack,
} from '@chakra-ui/react'
import { Plus, X } from 'lucide-react'
import { type ChangeEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChangeDisc } from './ChangeDisc'
import { FormLabelWithChangeIndicator } from './FormLabelWithChangeIndicator'
import { OrgFeaturesSsoConnections } from './OrgFeaturesSsoConnections'

// Utility to compare domain arrays (order-insensitive)
const areDomainsChanged = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) {
    return true
  }
  return [...a].sort().join(',') !== [...b].sort().join(',')
}

export type OrgFeaturesSsoProps = {
  /**
   * Notify parent that changes are saved
   * @param hasChanges
   */
  onHasChangesChange: (hasChanges: boolean) => void
  onCancel: () => void
  resetChanges?: boolean
  canRead?: boolean
  canEdit?: boolean
}

export const OrgFeaturesSso = ({
  onHasChangesChange,
  onCancel,
  resetChanges,
  canRead = true,
  canEdit = true,
}: OrgFeaturesSsoProps) => {
  const { t } = useTranslation('features')

  // Use organization context for API access
  const orgContext = useOrganization()

  const { orgFeatures } = orgContext

  const { data: features, isLoading, isError } = orgFeatures

  const [editSso, setEditSso] = useState<{
    enabled: boolean
    domain_aliases: string[]
  } | null>(null)
  const [savedSso, setSavedSso] = useState<{
    enabled: boolean
    domain_aliases: string[]
  } | null>(null)
  const [hasChanges, setHasChanges] = useState(false)
  const [domainInput, setDomainInput] = useState('')

  // Derived: whether there are any domain aliases to show
  const hasDomainAliases =
    Boolean(editSso?.domain_aliases.length) ||
    Boolean(savedSso?.domain_aliases.length)

  // Initialize local state from fetched SSO data
  useEffect(() => {
    if (features) {
      const initial = {
        enabled: Boolean(features.sso?.enabled),
        domain_aliases: features.sso?.domain_aliases || [],
      }
      setEditSso(initial)
      setSavedSso(initial)
    }
  }, [features])

  // Reset form when resetChanges prop changes
  useEffect(() => {
    if (resetChanges !== undefined && savedSso) {
      setEditSso({ ...savedSso })
      setDomainInput('')
    }
  }, [resetChanges, savedSso])

  // Compare editSso and savedSso for changes
  useEffect(() => {
    if (!editSso || !savedSso) {
      setHasChanges(false)
      return
    }
    const enabledChanged = editSso.enabled !== savedSso.enabled
    const domainsChanged = areDomainsChanged(
      editSso.domain_aliases,
      savedSso.domain_aliases,
    )
    const newHasChanges = enabledChanged || domainsChanged
    setHasChanges(newHasChanges)
    onHasChangesChange(newHasChanges)
  }, [editSso, savedSso, onHasChangesChange])

  const handleSsoSwitch = (e: ChangeEvent<HTMLInputElement>) => {
    setEditSso((prev) =>
      prev
        ? {
            ...prev,
            enabled: e.target.checked,
          }
        : prev,
    )
  }

  const handleAddDomain = () => {
    if (domainInput.trim() && editSso) {
      setEditSso((prev) =>
        prev
          ? {
              ...prev,
              domain_aliases: [...prev.domain_aliases, domainInput.trim()],
            }
          : prev,
      )
      setDomainInput('')
    }
  }

  const handleRemoveDomain = (domain: string) => {
    if (editSso) {
      setEditSso((prev) =>
        prev
          ? {
              ...prev,
              domain_aliases: prev.domain_aliases.filter((d) => d !== domain),
            }
          : prev,
      )
    }
  }

  const handleSaveClick = () => {
    if (editSso) {
      // Auto-add any domain that's in the input field before saving
      let updatedEditSso = { ...editSso }

      if (domainInput.trim()) {
        // Add the domain from input field to the list
        updatedEditSso = {
          ...updatedEditSso,
          domain_aliases: [
            ...updatedEditSso.domain_aliases,
            domainInput.trim(),
          ],
        }
        // Clear the input field
        setDomainInput('')
      }

      // Update the state with potentially new domain
      setEditSso(updatedEditSso)

      // Save with the updated data
      orgContext
        .patchOrganisationFeatures({ sso: { ...updatedEditSso } })
        .then(() => {
          setSavedSso({ ...updatedEditSso })
          setHasChanges(false)
          onHasChangesChange(false)
        })
    }
  }

  const handleCancelClick = () => {
    onCancel()
  }

  const isButtonsDisabled = isLoading || isError || !canEdit

  const status = features?.sso?.enabled
    ? t('organization.features.status.enabled')
    : t('organization.features.status.disabled')

  return (
    <Accordion.Item value="sso" disabled={isLoading || isError || !canRead}>
      <>
        <Accordion.ItemTrigger>
          <Box
            as="span"
            flex="1"
            textAlign="left"
            display="flex"
            alignItems="center"
            gap={2}
          >
            {t('organization.features.sso.title')}
            <Tag.Root
              size="sm"
              colorPalette={features?.sso?.enabled ? 'green' : 'gray'}
              aria-label={status}
            >
              <Tag.Label>{status}</Tag.Label>
            </Tag.Root>
            {!canEdit && (
              <Tooltip.Root positioning={{ placement: 'top' }}>
                <Tooltip.Trigger>
                  <Tag.Root size="sm" colorPalette="orange">
                    <Tag.Label>
                      {t('organization.features.readOnly.tag')}
                    </Tag.Label>
                  </Tag.Root>
                </Tooltip.Trigger>
                <Tooltip.Positioner>
                  <Tooltip.Content>
                    {t('organization.features.readOnly.tooltip')}
                  </Tooltip.Content>
                </Tooltip.Positioner>
              </Tooltip.Root>
            )}
            {hasChanges && (
              <Tag.Root
                size="sm"
                colorPalette="orange"
                ml={2}
                aria-label={String(
                  t('organization.features.unsavedChangesAria'),
                )}
              >
                <Tag.Label>
                  {t('organization.features.unsavedChanges')}
                </Tag.Label>
              </Tag.Root>
            )}
          </Box>
          <Accordion.ItemIndicator />
        </Accordion.ItemTrigger>
        <Accordion.ItemContent>
          <Accordion.ItemBody pb={4}>
            <Tabs.Root defaultValue="connections">
              <Tabs.List>
                <Tabs.Trigger value="connections">
                  {t('organization.features.sso.connections')}
                </Tabs.Trigger>
                <Tabs.Trigger value="settings">
                  {t('organization.features.sso.settings.title')}
                </Tabs.Trigger>
              </Tabs.List>

              <Tabs.Content value="connections">
                <OrgFeaturesSsoConnections />
              </Tabs.Content>
              <Tabs.Content value="settings">
                <VStack alignItems="start" gap={4} pl={4}>
                  <Field.Root display="flex" alignItems="start">
                    <FormLabelWithChangeIndicator
                      htmlFor="sso-enabled"
                      mb="0"
                      showChangeIndicator={
                        editSso?.enabled !== savedSso?.enabled
                      }
                    >
                      {t('organization.features.status.enabled')}
                    </FormLabelWithChangeIndicator>
                    <Box
                      borderRadius="md"
                      bg={
                        editSso?.enabled !== savedSso?.enabled
                          ? 'blue.50'
                          : undefined
                      }
                      p={1}
                      transition="background 0.2s"
                    >
                      <Switch.Root
                        id="sso-enabled"
                        data-testid="sso-enabled-switch"
                        checked={!!editSso?.enabled}
                        colorPalette={editSso?.enabled ? 'green' : 'gray'}
                        onCheckedChange={(details) =>
                          handleSsoSwitch({
                            target: { checked: details.checked },
                          } as ChangeEvent<HTMLInputElement>)
                        }
                        disabled={isButtonsDisabled}
                        data-changed={
                          editSso?.enabled !== savedSso?.enabled || undefined
                        }
                      >
                        <Switch.Control />
                        <Switch.HiddenInput />
                      </Switch.Root>
                    </Box>
                  </Field.Root>
                  {editSso?.enabled && (
                    <Field.Root>
                      <FormLabelWithChangeIndicator
                        showChangeIndicator={(() => {
                          if (!editSso || !savedSso) {
                            return false
                          }
                          return areDomainsChanged(
                            editSso.domain_aliases,
                            savedSso.domain_aliases,
                          )
                        })()}
                      >
                        {t(
                          'organization.features.sso.settings.domainAliases.label',
                        )}
                      </FormLabelWithChangeIndicator>
                      {hasDomainAliases ? (
                        <Box
                          borderRadius="md"
                          bg={(() => {
                            if (!editSso || !savedSso) {
                              return undefined
                            }
                            return areDomainsChanged(
                              editSso.domain_aliases,
                              savedSso.domain_aliases,
                            )
                              ? 'blue.50'
                              : undefined
                          })()}
                          p={1}
                          transition="background 0.2s"
                        >
                          <HStack wrap="wrap" gap={2}>
                            {editSso?.domain_aliases?.map((domain) => {
                              const isNew =
                                !savedSso?.domain_aliases.includes(domain)
                              return (
                                <Tag.Root
                                  key={domain}
                                  size="md"
                                  borderRadius="full"
                                  variant="solid"
                                  colorPalette="blue"
                                  gap={1.5}
                                >
                                  {isNew && (
                                    <ChangeDisc
                                      size={10}
                                      color="var(--color-white)"
                                      aria-label={String(
                                        t(
                                          'organization.features.fieldChangedAria',
                                        ),
                                      )}
                                    />
                                  )}
                                  <Tag.Label>{domain}</Tag.Label>
                                  <IconButton
                                    aria-label={
                                      t(
                                        'organization.features.sso.settings.domainAliases.removeDomain',
                                      ) ?? 'Remove domain'
                                    }
                                    size="xs"
                                    onClick={() => handleRemoveDomain(domain)}
                                    variant="ghost"
                                    color="white"
                                    _hover={{
                                      backgroundColor: 'transparent',
                                      color: 'blue.200',
                                    }}
                                    disabled={isButtonsDisabled}
                                  >
                                    <X size={12} />
                                  </IconButton>
                                </Tag.Root>
                              )
                            })}
                            {/* Show removed domains as strikethrough tags */}
                            {savedSso?.domain_aliases
                              .filter(
                                (domain) =>
                                  !editSso?.domain_aliases.includes(domain),
                              )
                              .map((domain) => (
                                <Tag.Root
                                  key={`${domain}-removed`}
                                  size="md"
                                  borderRadius="full"
                                  variant="outline"
                                  colorPalette="blue"
                                  textDecoration="line-through"
                                  opacity={0.7}
                                >
                                  <ChangeDisc
                                    size={10}
                                    aria-label={String(
                                      t(
                                        'organization.features.fieldChangedAria',
                                      ),
                                    )}
                                    style={{ marginRight: 2, flexShrink: 0 }}
                                  />
                                  <Tag.Label>{domain}</Tag.Label>
                                </Tag.Root>
                              ))}
                          </HStack>
                        </Box>
                      ) : null}
                      <Box>
                        <HStack mt={2}>
                          <Input
                            value={domainInput}
                            onChange={(e) => setDomainInput(e.target.value)}
                            placeholder={
                              t(
                                'organization.features.sso.settings.domainAliases.addDomainPlaceholder',
                              ) || ''
                            }
                            readOnly={!canEdit}
                            onKeyDown={(e) => {
                              if (
                                e.key === 'Enter' &&
                                domainInput.trim() &&
                                !isButtonsDisabled
                              ) {
                                handleAddDomain()
                              }
                            }}
                          />
                          <IconButton
                            aria-label={
                              t(
                                'organization.features.sso.settings.domainAliases.addDomain',
                              ) ?? 'Add Domain'
                            }
                            size="sm"
                            onClick={handleAddDomain}
                            disabled={!domainInput.trim() || isButtonsDisabled}
                          >
                            <Plus size={16} />
                          </IconButton>
                        </HStack>

                        {/* Helper text to explain the domain adding process */}
                        <Box
                          as="span"
                          fontSize="xs"
                          color="gray.500"
                          mt={1}
                          display="block"
                        >
                          {t(
                            'organization.features.sso.settings.domainAliases.domainInputHelper',
                          )}
                        </Box>
                      </Box>
                    </Field.Root>
                  )}
                  {hasChanges && (
                    <HStack gap={2} pt={2}>
                      <Button
                        colorPalette="blue"
                        size="sm"
                        onClick={handleSaveClick}
                        disabled={isButtonsDisabled}
                      >
                        {t('organization.features.actions.save')}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCancelClick}
                        disabled={isButtonsDisabled}
                      >
                        {t('organization.features.actions.cancel')}
                      </Button>
                    </HStack>
                  )}
                </VStack>
              </Tabs.Content>
            </Tabs.Root>
          </Accordion.ItemBody>
        </Accordion.ItemContent>
      </>
    </Accordion.Item>
  )
}
